import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, Trophy, Clock, Check, X, Ticket, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { getSportsMatches, getMySportsBets, placeSportsBet, casinoGetWallet } from '@/lib/actions';
import type { SportsMatch, SportsBet } from '@/lib/types';
import { cn } from '@/lib/utils';

type Selection = 'home' | 'draw' | 'away';

export function SportsBetting({ chips, onChips }: { chips: number; onChips: (c: number) => void }) {
  const { toast } = useToast();
  const [matches, setMatches] = useState<SportsMatch[]>([]);
  const [bets, setBets] = useState<SportsBet[]>([]);
  const [loading, setLoading] = useState(true);
  const [slip, setSlip] = useState<{ match: SportsMatch; selection: Selection; odds: number } | null>(null);
  const [stake, setStake] = useState('');
  const [placing, setPlacing] = useState(false);
  const [now, setNow] = useState(Date.now());

  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 15000); return () => clearInterval(t); }, []);
  const hasStarted = (m: SportsMatch) => !!m.date && !isNaN(new Date(m.date).getTime()) && new Date(m.date).getTime() <= now;

  const refresh = async () => {
    const [mRes, bRes] = await Promise.all([getSportsMatches(), getMySportsBets()]);
    if (mRes.success) setMatches(mRes.matches);
    if (bRes.success) setBets(bRes.bets);
    setLoading(false);
  };
  useEffect(() => { refresh(); }, []);

  const openMatches = useMemo(() => matches.filter((m) => m.status === 'open'), [matches]);

  const selectionName = (m: SportsMatch, s: Selection) => s === 'home' ? m.team1 : s === 'away' ? m.team2 : 'Empate';

  const confirmBet = async () => {
    if (!slip) return;
    const amt = Math.floor(Number(stake));
    if (!amt || amt <= 0) { toast({ title: 'Importe no válido', variant: 'destructive' }); return; }
    if (amt > chips) { toast({ title: 'Fichas insuficientes', variant: 'destructive' }); return; }
    setPlacing(true);
    const res = await placeSportsBet(slip.match.id, slip.selection, amt);
    if (res.success) {
      toast({ title: '¡Apuesta realizada!', description: res.message });
      const w = await casinoGetWallet();
      if (w.success) onChips(w.chips);
      setSlip(null); setStake('');
      refresh();
    } else toast({ title: 'Error', description: res.message, variant: 'destructive' });
    setPlacing(false);
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-amber-400" /></div>;

  const STATUS = {
    pending: { label: 'Pendiente', cls: 'bg-amber-500/20 text-amber-400', icon: <Clock className="h-3.5 w-3.5" /> },
    won: { label: 'Ganada', cls: 'bg-green-500/20 text-green-400', icon: <Check className="h-3.5 w-3.5" /> },
    lost: { label: 'Perdida', cls: 'bg-red-500/20 text-red-400', icon: <X className="h-3.5 w-3.5" /> },
  } as const;

  const pendingCount = bets.filter((b) => b.status === 'pending').length;

  return (
    <Tabs defaultValue="matches" className="space-y-4">
      <TabsList className="bg-black/40">
        <TabsTrigger value="matches">Partidos ({openMatches.length})</TabsTrigger>
        <TabsTrigger value="mybets">Mis apuestas {pendingCount > 0 && <span className="ml-1 rounded-full bg-amber-500 px-1.5 text-[10px] font-bold text-black">{pendingCount}</span>}</TabsTrigger>
      </TabsList>

      {/* Partidos */}
      <TabsContent value="matches" className="space-y-3">
        {openMatches.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-white/8 py-16 text-muted-foreground"><Trophy className="mb-3 h-10 w-10 opacity-40" /><p className="text-sm">No hay partidos disponibles ahora mismo.</p></div>
        ) : openMatches.map((m) => {
          const started = hasStarted(m);
          const active = slip?.match.id === m.id && !started;
          return (
            <div key={m.id} className={cn('rounded-2xl border bg-black/40 p-4', started ? 'border-white/10 opacity-80' : 'border-amber-500/20')}>
              <div className="mb-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-bold">{m.team1} <span className="text-muted-foreground text-sm">vs</span> {m.team2}</p>
                  {m.date && <p className="text-xs text-muted-foreground">{new Date(m.date).toLocaleString('es-ES')}</p>}
                </div>
                {started
                  ? <span className="flex shrink-0 items-center gap-1 rounded-full bg-zinc-700/60 px-2.5 py-1 text-[0.7rem] font-bold text-zinc-200"><Lock className="h-3 w-3" /> En juego</span>
                  : <Trophy className="h-5 w-5 shrink-0 text-amber-400/60" />}
              </div>

              {started ? (
                <div className="rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-center text-sm text-muted-foreground">El partido ya ha comenzado. Apuestas cerradas.</div>
              ) : (<>
              <div className="grid grid-cols-3 gap-2">
                {([['home', m.oddsHome, '1'], ['draw', m.oddsDraw, 'X'], ['away', m.oddsAway, '2']] as [Selection, number, string][]).map(([sel, odd, lbl]) => (
                  <button key={sel} onClick={() => { setSlip({ match: m, selection: sel, odds: odd }); setStake(''); }}
                    className={cn('flex flex-col items-center rounded-xl border px-2 py-2.5 transition-all',
                      active && slip?.selection === sel ? 'border-amber-400 bg-amber-500/20 ring-2 ring-amber-400' : 'border-white/10 bg-white/5 hover:bg-white/10')}>
                    <span className="text-[0.65rem] uppercase tracking-wide text-muted-foreground">{lbl} · {selectionName(m, sel)}</span>
                    <span className="text-lg font-black text-amber-300">{odd.toFixed(2)}</span>
                  </button>
                ))}
              </div>

              {/* Boleto inline */}
              {active && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mt-3 overflow-hidden rounded-xl border border-amber-400/30 bg-amber-500/5 p-3">
                  <div className="flex items-center gap-2 text-sm">
                    <Ticket className="h-4 w-4 text-amber-400" />
                    <span>Apuestas a <b>{selectionName(m, slip!.selection)}</b> @ <b className="text-amber-300">{slip!.odds.toFixed(2)}</b></span>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <Input type="number" min={1} value={stake} onChange={(e) => setStake(e.target.value)} placeholder="Fichas a apostar" className="h-9" />
                    <Button onClick={confirmBet} disabled={placing} className="shrink-0 bg-gradient-to-r from-amber-400 to-amber-600 font-bold text-black">
                      {placing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apostar'}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setSlip(null)}>Cancelar</Button>
                  </div>
                  {Number(stake) > 0 && <p className="mt-1.5 text-xs text-muted-foreground">Ganancia potencial: <b className="text-green-400">{Math.round(Number(stake) * slip!.odds).toLocaleString('es-ES')} fichas</b></p>}
                </motion.div>
              )}
              </>)}
            </div>
          );
        })}
      </TabsContent>

      {/* Mis apuestas */}
      <TabsContent value="mybets" className="space-y-2">
        {bets.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-white/8 py-16 text-muted-foreground"><Ticket className="mb-3 h-10 w-10 opacity-40" /><p className="text-sm">Aún no has hecho ninguna apuesta.</p></div>
        ) : bets.map((b) => {
          const st = STATUS[b.status];
          return (
            <div key={b.id} className="flex items-center gap-3 rounded-2xl border border-white/8 bg-card/40 p-4">
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold">{b.team1} vs {b.team2}</p>
                <p className="text-xs text-muted-foreground">
                  {b.selection === 'home' ? b.team1 : b.selection === 'away' ? b.team2 : 'Empate'} @ {b.odds.toFixed(2)} · {b.stake} fichas
                </p>
              </div>
              <div className="shrink-0 text-right">
                <Badge className={cn('gap-1', st.cls)}>{st.icon}{st.label}</Badge>
                <p className={cn('mt-1 text-sm font-black', b.status === 'won' ? 'text-green-400' : b.status === 'lost' ? 'text-red-400' : 'text-muted-foreground')}>
                  {b.status === 'won' ? `+${b.payout.toLocaleString('es-ES')}` : b.status === 'lost' ? `-${b.stake.toLocaleString('es-ES')}` : `→ ${b.potentialPayout.toLocaleString('es-ES')}`}
                </p>
              </div>
            </div>
          );
        })}
      </TabsContent>
    </Tabs>
  );
}
